import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowUp, ArrowDown, Trash2, Eye, EyeOff, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { HeroSlide, WebinarTile, MARKER, type HeroCTA } from '@/components/HomePage';
import type { HeroSlideDef, CreatorProduct } from '@/services/creatorApi';

// Homepage hero editor (docs/PLAN_homepage_hero_admin.md, step 3). Edits the
// ordered slide list the homepage carousel reads; product slides carry copy,
// the product itself is merged live on read. Static slides (brand, audition,
// creator store) keep their coded copy and can only be reordered or hidden.

type SearchHit = { _id: string; title: string; published?: boolean; eventDate?: string | null; creatorEmail?: string; price?: number; memberPriceCents?: number | null; description?: string };

const STATIC_LABEL: Record<string, string> = { brand: 'Brand: Everything you need to book more work', audition: 'Tool: Audition Enhancer', creator: 'Creator store: Turn what you know into income', sites: 'Hey Joe Sites: Your acting site, under your own name' };
const KICKER: Record<string, string> = { yellow: 'var(--hj-yellow-ink)', pink: 'var(--hj-pink-ink)', ink: 'var(--hj-ink-3)' };
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

// Pre-fill copy from the product so an admin edits instead of writing from scratch.
function draftFromProduct(p: SearchHit): HeroSlideDef {
  const title = String(p.title || '').trim();
  let top = title, marker = '';
  const colon = title.indexOf(':');
  if (colon > 0) { top = title.slice(0, colon).trim(); marker = title.slice(colon + 1).trim(); }
  else { const w = title.split(/\s+/); if (w.length > 2) { marker = w.slice(-2).join(' '); top = w.slice(0, -2).join(' '); } }
  if (marker && !/[.!?]$/.test(marker)) marker += '.';
  const first = String(p.description || '').split(/\n|\. /)[0].trim();
  const price = (p.price || 0) === 0 ? 'Free.' : (p.memberPriceCents === 0 ? `$${((p.price || 0) / 100).toFixed(0)}, free for Plus members.` : (typeof p.memberPriceCents === 'number' ? `$${((p.price || 0) / 100).toFixed(0)}, $${(p.memberPriceCents / 100).toFixed(0)} for Plus members.` : `$${((p.price || 0) / 100).toFixed(0)}.`));
  return {
    id: `p_${p._id.slice(-6)}_${Date.now().toString(36)}`, enabled: true, kind: 'product', productId: p._id,
    kickerPrefix: p.eventDate ? 'Live event' : 'Featured', kickerColor: 'yellow',
    headlineTop: top, headlineMarker: marker, markerFirst: false,
    subhead: `${first}${first && !/[.!?]$/.test(first) ? '.' : ''} ${price}`.trim(),
    primaryCta: { label: p.eventDate ? 'Reserve your spot' : 'Get the course', to: '/course/{productId}' },
    secondaryCta: { label: 'All upcoming events', to: '/calendar' },
    hideAfterEvent: true,
  };
}

function Preview({ d }: { d: HeroSlideDef }) {
  const product = d.product || null;
  const to = (d.primaryCta?.to || '/course/{productId}').replace('{productId}', d.productId || '');
  const dateShort = fmtDate(product?.eventDate);
  const kicker = dateShort ? `${d.kickerPrefix || 'Live event'} · ${dateShort}` : (d.kickerPrefix || 'Featured event');
  const topEl = <span style={{ display: 'block' }}>{d.headlineTop}{d.markerFirst ? '' : ' '}</span>;
  const markerEl = <span style={MARKER}>{d.headlineMarker}</span>;
  const headline = d.markerFirst ? <><span style={{ display: 'block' }}>{markerEl}</span>{topEl}</> : <>{topEl}{markerEl}</>;
  const ctas: HeroCTA[] = [{ label: d.primaryCta?.label || 'Reserve your spot', to, primary: true }];
  if (d.secondaryCta?.label) ctas.push({ label: d.secondaryCta.label, to: d.secondaryCta.to });
  return (
    <div style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', padding: 24, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ transform: 'scale(0.72)', transformOrigin: 'top left', width: '139%' }}>
        <HeroSlide kicker={kicker} kickerColor={KICKER[d.kickerColor || 'yellow']} headline={headline} subhead={(d.subhead || '').replace('{date}', '')} ctas={ctas}
          tile={<WebinarTile product={product as CreatorProduct | null} creator={d.creator || null} to={to} full={d.tileStyle === 'full'} />} />
      </div>
    </div>
  );
}

export function HomepageHeroPanel() {
  const { toast } = useToast();
  const [slides, setSlides] = useState<HeroSlideDef[]>([]);
  const [meta, setMeta] = useState<{ fromDefault: boolean; updatedAt: string | null; updatedBy: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin/homepage/hero', { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setSlides(d.slides || []); setMeta({ fromDefault: !!d.fromDefault, updatedAt: d.updatedAt, updatedBy: d.updatedBy }); setDirty(false);
    } catch (e: any) { toast({ title: 'Could not load the hero', description: e.message, variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (id: string, patch: Partial<HeroSlideDef>) => { setSlides(s => s.map(x => (x.id === id ? { ...x, ...patch } : x))); setDirty(true); };
  const move = (i: number, dir: -1 | 1) => { setSlides(s => { const n = [...s]; const j = i + dir; if (j < 0 || j >= n.length) return s; [n[i], n[j]] = [n[j], n[i]]; return n; }); setDirty(true); };
  const remove = (id: string) => { setSlides(s => s.filter(x => x.id !== id)); setDirty(true); if (editing === id) setEditing(null); };

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try { const r = await fetch(`/api/admin/products/search?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' }); const d = r.ok ? await r.json() : { products: [] }; setHits(d.products || []); }
    catch { setHits([]); } finally { setSearching(false); }
  };
  const add = (p: SearchHit) => {
    if (slides.length >= 8) { toast({ title: 'Eight slides is the cap', variant: 'destructive' }); return; }
    const d = draftFromProduct(p);
    setSlides(s => [...s, { ...d, product: p as unknown as CreatorProduct, creator: null }]); setDirty(true); setEditing(d.id); setHits([]); setQ('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = slides.map(({ product, creator, warning, ...rest }) => rest);
      const r = await fetch('/api/admin/homepage/hero', { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slides: body }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Save failed');
      setSlides(d.slides || slides); setDirty(false); setMeta(m => ({ fromDefault: false, updatedAt: new Date().toISOString(), updatedBy: m?.updatedBy || null }));
      toast({ title: 'Homepage updated', description: 'Live within a minute (60s cache per server).' });
    } catch (e: any) { toast({ title: 'Not saved', description: e.message, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const editingSlide = slides.find(s => s.id === editing) || null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Homepage hero</CardTitle>
          <CardDescription>
            The "What's on Hey Joe Plus" carousel. Order here is the order on the page. Product slides pull the product live; the copy is yours.
            {meta && (meta.fromDefault ? ' Showing the built-in default (never saved).' : ` Last saved ${meta.updatedAt ? new Date(meta.updatedAt).toLocaleString() : ''}${meta.updatedBy ? ` by ${meta.updatedBy}` : ''}.`)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <div className="space-y-2">
              {slides.map((s, i) => (
                <div key={s.id} className="flex items-center gap-3 rounded-lg border p-3" style={{ opacity: s.enabled ? 1 : 0.55, background: editing === s.id ? 'rgba(255,214,10,0.08)' : undefined }}>
                  <div className="flex flex-col gap-1">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30"><ArrowUp size={14} /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === slides.length - 1} className="disabled:opacity-30"><ArrowDown size={14} /></button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm truncate">{s.kind === 'static' ? STATIC_LABEL[s.staticKey || ''] : (s.product?.title || `Product ${s.productId}`)}</span>
                      <Badge variant="outline">{s.kind === 'static' ? 'static' : 'product'}</Badge>
                      {s.kind === 'product' && s.product?.eventDate && <Badge variant="secondary">{fmtDate(s.product.eventDate)}</Badge>}
                      {s.warning && <Badge variant="destructive">{s.warning}</Badge>}
                      {!s.enabled && <Badge variant="secondary">hidden</Badge>}
                    </div>
                    {s.kind === 'product' && <div className="text-xs text-muted-foreground truncate">{s.headlineTop} <em>{s.headlineMarker}</em></div>}
                  </div>
                  <div className="flex items-center gap-1">
                    {s.kind === 'product' && <Button size="sm" variant={editing === s.id ? 'default' : 'outline'} onClick={() => setEditing(editing === s.id ? null : s.id)}>{editing === s.id ? 'Close' : 'Edit'}</Button>}
                    <Button size="sm" variant="ghost" onClick={() => update(s.id, { enabled: !s.enabled })} title={s.enabled ? 'Hide' : 'Show'}>{s.enabled ? <Eye size={15} /> : <EyeOff size={15} />}</Button>
                    {s.kind === 'product' && <Button size="sm" variant="ghost" onClick={() => remove(s.id)} title="Remove"><Trash2 size={15} /></Button>}
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') search(); }} placeholder="Add a product slide: search by title or creator email" style={{ maxWidth: 420 }} />
                <Button size="sm" variant="outline" onClick={search} disabled={searching}>{searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus size={14} className="mr-1" />Find</>}</Button>
                <div className="flex-1" />
                <Button onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save homepage'}</Button>
              </div>
              {hits.length > 0 && (
                <div className="rounded-lg border divide-y">
                  {hits.slice(0, 8).map(h => (
                    <button key={h._id} type="button" onClick={() => add(h)} className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2">
                      <span className="truncate flex-1">{h.title}</span>
                      {h.eventDate && <Badge variant="secondary">{fmtDate(h.eventDate)}</Badge>}
                      {!h.published && <Badge variant="destructive">draft</Badge>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {editingSlide && editingSlide.kind === 'product' && (
        <Card>
          <CardHeader><CardTitle>Edit slide: {editingSlide.product?.title || editingSlide.productId}</CardTitle><CardDescription>The date in the kicker comes from the product's next session. Use {'{productId}'} in a link to point at this product.</CardDescription></CardHeader>
          <CardContent className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-3">
              <label className="block text-sm"><span className="text-muted-foreground">Kicker</span><Input value={editingSlide.kickerPrefix || ''} onChange={e => update(editingSlide.id, { kickerPrefix: e.target.value })} /></label>
              <label className="block text-sm"><span className="text-muted-foreground">Kicker color</span>
                <select value={editingSlide.kickerColor || 'yellow'} onChange={e => update(editingSlide.id, { kickerColor: e.target.value as any })} className="block w-full border rounded-md px-3 py-2 text-sm bg-background">
                  <option value="yellow">Yellow</option><option value="pink">Pink</option><option value="ink">Grey</option>
                </select></label>
              <label className="block text-sm"><span className="text-muted-foreground">Headline, plain part</span><Input value={editingSlide.headlineTop || ''} onChange={e => update(editingSlide.id, { headlineTop: e.target.value })} /></label>
              <label className="block text-sm"><span className="text-muted-foreground">Headline, highlighted part</span><Input value={editingSlide.headlineMarker || ''} onChange={e => update(editingSlide.id, { headlineMarker: e.target.value })} /></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editingSlide.markerFirst} onChange={e => update(editingSlide.id, { markerFirst: e.target.checked })} /> Highlighted part first</label>
              <label className="block text-sm"><span className="text-muted-foreground">Subhead</span><textarea value={editingSlide.subhead || ''} onChange={e => update(editingSlide.id, { subhead: e.target.value })} rows={3} className="block w-full border rounded-md px-3 py-2 text-sm bg-background" /></label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-sm"><span className="text-muted-foreground">Button label</span><Input value={editingSlide.primaryCta?.label || ''} onChange={e => update(editingSlide.id, { primaryCta: { label: e.target.value, to: editingSlide.primaryCta?.to || '/course/{productId}' } })} /></label>
                <label className="block text-sm"><span className="text-muted-foreground">Button link</span><Input value={editingSlide.primaryCta?.to || ''} onChange={e => update(editingSlide.id, { primaryCta: { label: editingSlide.primaryCta?.label || 'Reserve your spot', to: e.target.value } })} /></label>
                <label className="block text-sm"><span className="text-muted-foreground">Second link label</span><Input value={editingSlide.secondaryCta?.label || ''} onChange={e => update(editingSlide.id, { secondaryCta: e.target.value ? { label: e.target.value, to: editingSlide.secondaryCta?.to || '/calendar' } : null })} /></label>
                <label className="block text-sm"><span className="text-muted-foreground">Second link</span><Input value={editingSlide.secondaryCta?.to || ''} onChange={e => update(editingSlide.id, { secondaryCta: { label: editingSlide.secondaryCta?.label || 'All upcoming events', to: e.target.value } })} /></label>
              </div>
              <label className="block text-sm"><span className="text-muted-foreground">Right side</span>
                <select value={editingSlide.tileStyle || 'card'} onChange={e => update(editingSlide.id, { tileStyle: e.target.value as 'card' | 'full' })} className="block w-full border rounded-md px-3 py-2 text-sm bg-background">
                  <option value="card">Event card in the frame</option><option value="full">Cover image fills the frame</option>
                </select></label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editingSlide.hideAfterEvent !== false} onChange={e => update(editingSlide.id, { hideAfterEvent: e.target.checked })} /> Hide this slide automatically once the event has passed</label>
            </div>
            <div><div className="text-xs text-muted-foreground mb-2">Preview (desktop, scaled)</div><Preview d={editingSlide} /></div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
